import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  InputAdornment,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { Element } from '../../types/Element';
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks';
import { 
  fetchElements, 
  createElement, 
  updateElement, 
  deleteElement, 
  clearElementError,
  selectElements,
  selectElementsLoading,
  selectElementsError 
} from '../../store/slices/elementSlice';

interface ElementManagementProps {
  onElementSelect?: (element: Element) => void;
}

interface ElementFormData extends Omit<Element, 'id'> {
  id?: string;
}

const initialFormData: ElementFormData = {
  symbol: '',
  name: '',
  atomicNumber: 0,
  atomicWeight: 0,
  category: 'macronutrient',
  valence: 0,
};

const elementCategories = [
  { value: 'macronutrient', label: 'Macronutrient' },
  { value: 'secondary', label: 'Secondary Nutrient' },
  { value: 'micronutrient', label: 'Micronutrient' },
  { value: 'beneficial', label: 'Beneficial Element' },
  { value: 'other', label: 'Other' },
];

const ElementManagement: React.FC<ElementManagementProps> = ({ onElementSelect }) => {
  const dispatch = useAppDispatch();
  const elements = useAppSelector(selectElements);
  const loading = useAppSelector(selectElementsLoading);
  const error = useAppSelector(selectElementsError);
  
  const [formData, setFormData] = useState<ElementFormData>(initialFormData);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  useEffect(() => {
    dispatch(fetchElements());
  }, [dispatch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    
    if (name === 'atomicNumber' || name === 'atomicWeight' || name === 'valence') {
      setFormData({ ...formData, [name]: parseFloat(value) || 0 });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleCategoryChange = (e: SelectChangeEvent<string>) => {
    setFormData({ ...formData, category: e.target.value });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const filteredElements = elements.filter(
    (element: Element) =>
      element.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      element.symbol.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const resetForm = () => {
    setFormData(initialFormData);
    setEditMode(false);
  };

  const handleOpenDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleEdit = (element: Element) => {
    setFormData({
      id: element.atomicNumber.toString(),
      symbol: element.symbol,
      name: element.name,
      atomicNumber: element.atomicNumber,
      atomicWeight: element.atomicWeight,
      category: element.category || 'other',
      valence: element.valence || 0,
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDelete = async (elementId: string) => {
    if (!window.confirm('Are you sure you want to delete this element?')) {
      return;
    }

    try {
      await dispatch(deleteElement(elementId)).unwrap();
      setSnackbarMessage('Element deleted successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error deleting element:', err);
      setSnackbarMessage('Failed to delete element');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const validateForm = () => {
    if (!formData.symbol.trim()) return 'Symbol is required';
    if (!formData.name.trim()) return 'Name is required';
    if (formData.atomicNumber <= 0) return 'Atomic number must be positive';
    if (formData.atomicWeight <= 0) return 'Atomic weight must be positive';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setSnackbarMessage(validationError);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      if (editMode && formData.id) {
        await dispatch(updateElement({
          id: formData.id,
          element: {
            symbol: formData.symbol,
            name: formData.name,
            atomicNumber: formData.atomicNumber,
            atomicWeight: formData.atomicWeight,
            category: formData.category,
            valence: formData.valence
          }
        })).unwrap();
        setSnackbarMessage('Element updated successfully');
      } else {
        await dispatch(createElement({
          symbol: formData.symbol,
          name: formData.name,
          atomicNumber: formData.atomicNumber,
          atomicWeight: formData.atomicWeight,
          category: formData.category,
          valence: formData.valence
        })).unwrap();
        setSnackbarMessage('Element added successfully');
      }
      
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving element:', err);
      setSnackbarMessage('Failed to save element');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Function to handle selecting an element for salt composition
  const handleSelectElement = (element: Element) => {
    if (onElementSelect) {
      onElementSelect(element);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Hydroponics Elements</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            placeholder="Search elements..."
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
          >
            Add Element
          </Button>
        </Box>
      </Box>

      {error && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'error.light' }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      )}

      {loading && !dialogOpen ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Symbol</TableCell>
                <TableCell>Name</TableCell>
                <TableCell align="center">Atomic Number</TableCell>
                <TableCell align="center">Atomic Weight</TableCell>
                <TableCell align="center">Category</TableCell>
                <TableCell align="center">Valence</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredElements.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No elements found
                  </TableCell>
                </TableRow>
              ) : (
                filteredElements.map((element: Element) => (
                  <TableRow key={element.atomicNumber}>
                    <TableCell>{element.symbol}</TableCell>
                    <TableCell>{element.name}</TableCell>
                    <TableCell align="center">{element.atomicNumber}</TableCell>
                    <TableCell align="center">{element.atomicWeight}</TableCell>
                    <TableCell align="center">
                      {element.category
                        ? element.category.charAt(0).toUpperCase() +
                          element.category.slice(1)
                        : 'N/A'}
                    </TableCell>
                    <TableCell align="center">{element.valence || 'N/A'}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Tooltip title="Edit element">
                          <IconButton
                            onClick={() => handleEdit(element)}
                            size="small"
                            color="primary"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete element">
                          <IconButton
                            onClick={() => handleDelete(element.atomicNumber.toString())}
                            size="small"
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {onElementSelect && (
                          <Tooltip title="Select element">
                            <IconButton
                              onClick={() => handleSelectElement(element)}
                              size="small"
                              color="default"
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>
          {editMode ? 'Edit Element' : 'Add New Element'}
        </DialogTitle>
        <DialogContent>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              mt: 2,
              minWidth: '400px',
            }}
          >
            <TextField
              label="Symbol"
              name="symbol"
              value={formData.symbol}
              onChange={handleInputChange}
              required
              fullWidth
            />
            <TextField
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              fullWidth
            />
            <TextField
              label="Atomic Number"
              name="atomicNumber"
              type="number"
              value={formData.atomicNumber || ''}
              onChange={handleInputChange}
              required
              fullWidth
              inputProps={{ step: 1 }}
            />
            <TextField
              label="Atomic Weight"
              name="atomicWeight"
              type="number"
              value={formData.atomicWeight || ''}
              onChange={handleInputChange}
              required
              fullWidth
              inputProps={{ step: 0.001 }}
            />
            <FormControl fullWidth>
              <InputLabel id="category-label">Category</InputLabel>
              <Select
                labelId="category-label"
                name="category"
                value={formData.category || ''}
                label="Category"
                onChange={handleCategoryChange}
              >
                {elementCategories.map((category) => (
                  <MenuItem key={category.value} value={category.value}>
                    {category.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Valence"
              name="valence"
              type="number"
              value={formData.valence || ''}
              onChange={handleInputChange}
              fullWidth
              inputProps={{ step: 1 }}
              helperText="Common valence state"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ElementManagement;
